// types
import { DoctorAvailabilityData } from 'data-types';

// components
import Skeletonize from '../../Skeletons/Skeletonize';


// utils
import { translate } from '../../utils/translator';
import { clientConfig } from '../../config';


type Props = {
	language: string;
	doctorAvailabilityData: DoctorAvailabilityData;
	onDoctorCardClick: (doctor: DoctorAvailabilityData) => void;
	isLoading: boolean;
	disableSkeleton?: boolean;
	layout?: 'choose-doctor' | 'schedule-appointment';
	layoutWithDoctors: boolean;
}


const DoctorCard: React.FC<Props> = ({ language, doctorAvailabilityData, onDoctorCardClick, layout = 'choose-doctor', isLoading, disableSkeleton = false, layoutWithDoctors }) => {
	return (
		<div 
			id="doctor-card"
			className={`
				grid 
				${layout === "choose-doctor" && `
					bg-[#FFFFFF]
					rounded-[16px]
					p-[30px]
					justify-items-center
					grid-rows-[repeat(4,auto)_1fr]
				`}
				${layout === "schedule-appointment" && `
					grid-rows-[auto_auto_auto]
					md:grid-rows-[min-content_min-content_1fr]
					grid-cols-[min-content_1fr] 
					items-start
					justify-start
					gap-x-[12px] md:gap-x-[30px]
					gap-y-[7px] md:gap-y-[10px]
				`}
			`}
		>
			<Skeletonize
				isLoading={isLoading}
				className={`${layout === "choose-doctor" && "w-[125px] h-[125px] mb-[20px] rounded-[100%]"} ${layout === "schedule-appointment" && "w-[92px] h-[92px] col-start-1 row-start-1 row-end-3 md:row-end-[-1] rounded-[16px]"} overflow-hidden md:h-[132px] md:w-[132px]`}
				animation='pulse syncWave'
				rounded='full'
				disableSkeleton={disableSkeleton}
			>
				<div
					id="doctor-card-image-container"
					className={`
						overflow-hidden
						md:w-[132px]
						md:h-[132px]
						animate-fade-in-up-10
						border-[#ecececab]
						border-solid
						border-[1px]
						bg-gray-100
						${layout === "choose-doctor" && `
							w-[125px] 
							h-[125px] 
							rounded-[100%]
							mb-[20px]
						`}
						${layout === "schedule-appointment" && `	
							w-[92px] 
							h-[92px] 
							rounded-[16px]
							col-start-1
							row-start-1
							row-end-3 md:row-end-[-1]
						`}
					`}
				>
					<img
						src={clientConfig.IMAGES_URL + doctorAvailabilityData.imageSrc} 
						alt={doctorAvailabilityData.name} 
						className="
							object-cover
							w-full
							text-center
							text-[10px]
						"
					/>
				</div>
			</Skeletonize>

			<Skeletonize
				isLoading={isLoading}
				className={`${layout === "choose-doctor" && "!mb-[5px] h-[18px] w-1/2"} ${layout === "schedule-appointment" && "row-start-1 self-end md:self-start h-[20px] md:h-[30px] w-1/2"}`}
				animation='pulse syncWave'
				disableSkeleton={disableSkeleton}
			>
				<h3
					id="doctor-card-name"
					className={`
						capitalize
						text-[#000000]
						animate-fade-in-up-10
						h-fit
						${layout === "choose-doctor" && `
							font-roboto
							text-[18px]
							mb-[5px]
							font-bold
							text-center
						`}
						${layout === "schedule-appointment" && `
							font-semibold
							text-[20px] md:text-[30px]
							self-end md:self-start
							row-start-1
							leading-[1]
						`}
					`}
				>
					{doctorAvailabilityData.name}
				</h3>
			</Skeletonize>

			<Skeletonize
				isLoading={isLoading}
				className={`${layout === "choose-doctor" && "mb-[20px] h-[12px]"} ${layout === "schedule-appointment" && "row-start-2"} w-1/3 h-[12px] my-[9px] mx-[5px]`}
				skeletonCount={1}
				animation='pulse syncWave'
				disableSkeleton={disableSkeleton}
			>
				{doctorAvailabilityData.expertise && (
					<h5
						id="doctor-card-specialty"
						className={`
							animate-fade-in-up-10
							px-[9px]
							py-[5px]
							inline-block
							text-center
							h-fit
							max-w-[80%]
							leading-[1.4]
							text-[12px]
							font-roboto
							font-normal
							capitalize
							bg-[#E8F2E6]
							text-[#000000]
							rounded-[50px]
							${layout === "choose-doctor" && `
								mb-[20px]
								truncate
							`}
							${layout === "schedule-appointment" && `
								row-start-2
								truncate
								w-fit
								h-fit
							`}
						`}
					>
						{doctorAvailabilityData.expertise}
					</h5>
				)}
			</Skeletonize>

			<Skeletonize
				isLoading={isLoading}
				className={`${layout === "choose-doctor" && "mb-[20px] h-[14px] w-full"} ${layout === "schedule-appointment" && "row-start-3 w-[100%] md:w-[60%] col-start-1 md:col-start-2 col-end-3 my-[5px] md:my-0"}`}
				animation='syncWave pulse'
				disableSkeleton={disableSkeleton}
			>
				<p 
					id="doctor-card-brief"
					className={`
						animate-fade-in-up-10
						text-[14px]
						font-roboto
						font-normal
						text-center
						text-[#000000]
						leading-[1.4]
						${layout === "choose-doctor" && `
							mb-[20px]
						`}
						${layout === "schedule-appointment" && `
							row-start-3
							text-start
							max-w-[100%] md:max-w-[60%]
							col-start-1 md:col-start-2
							col-end-3
							py-[${doctorAvailabilityData.brief ? "5" : "0"}px] md:py-0

						`}
					`}
				>
					{doctorAvailabilityData.brief}
				</p>
			</Skeletonize>

			<Skeletonize
				isLoading={isLoading}
				className={`${layout === "schedule-appointment" && "hidden"} mb-[20px] h-[48px] w-1/2 rounded-[50px] overflow-hidden`}
				rounded='full'
				animation='pulse syncWave'
				disableSkeleton={disableSkeleton}
			>
				<button
					id="doctor-card-book-appointment-button"
					className={`
						animate-fade-in-up-10
						cursor-pointer
						capitalize
						bg-[#0A9281]
						text-[#FFFFFF]
						rounded-[50px]
						border-none
						px-6
						py-3
						row-start-[-1]
						text-[14px]
						shadow-[0_16.25px_25.99px_0_rgba(42,163,70,0.2)]
						self-end
						${layout === "schedule-appointment" && `
							hidden
						`}
					`}
					onClick={() => onDoctorCardClick(doctorAvailabilityData)}
				>
					{translate(language, 'choose_doctor', layoutWithDoctors ? 'doctor_card/book_appointment_with_doctors' : 'doctor_card/book_appointment_without_doctors')}
				</button>
			</Skeletonize>
		</div>
	);
}

export default DoctorCard;
